import {
  IconBrandAzure,
  IconCalendar,
  IconCheck,
  IconChevronDown,
  IconChevronRight,
  IconClock,
  IconCopy,
  IconHistory,
  IconServer,
} from "@tabler/icons-react";
import {
  useSuspenseQuery,
  useQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  useNavigate,
} from "@tanstack/react-router";
import {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { toast } from "sonner";

import type { RouterOutputs } from "@/infrastructure/trpc/router";

import { AgentHeader } from "@/agents/components/agent-header";
import { DeleteAgentDialog } from "@/agents/components/dialogs/delete-agent-dialog";
import { EditAgentDialog } from "@/agents/components/dialogs/edit-agent-dialog";
import {
  calculateDuration,
  formatDuration,
} from "@/agents/components/run-report/shared/formatters";
import { env } from "@/env/public-env";
import { useCurrentTime } from "@/hooks/use-current-time";
import { useTRPC } from "@/infrastructure/trpc/react";
import { AppLayout, PageBreadcrumb } from "@/shared/layout";
import {
  DataTable,
  type DataTableColumnDef,
} from "@/shared/tables/data-table";
import { Button } from "@/shared/ui/button";
import { Card, CardContent } from "@/shared/ui/card";
import { ClientDate } from "@/shared/ui/client-date";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shared/ui/collapsible";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select";
import { StatusDot } from "@/shared/ui/status-dot";
import {
  AgentRunStatus,
  AgentRunOutcome,
  AgentTypes,
  SchedulePresets,
  type AgentRunStatusValue,
  type AgentRunOutcomeValue,
  type AgentRunResult,
  type AzureDevOpsPipelineAgentConfig,
  type K8sIssuesAgentConfig,
  type K8sOptimizationAgentConfig,
  type OtelReceiverAgentConfig,
} from "@vivus/common";

type AgentRun =
  RouterOutputs["agents"]["listRuns"]["runs"][number];

type AgentRunsSearch = {
  page?: number;
  size?: number;
  status?: AgentRunStatusValue | "all";
};

export const Route = createFileRoute(
  "/app/project/$id/agents/$agentId/"
)({
  validateSearch: (
    search: Record<string, unknown>
  ): AgentRunsSearch => {
    const result: AgentRunsSearch = {};
    if (search.page) result.page = Number(search.page);
    if (search.size) result.size = Number(search.size);
    if (search.status)
      result.status = search.status as
        | AgentRunStatusValue
        | "all";
    return result;
  },
  loaderDeps: ({ search }) => ({
    page: search.page ?? 1,
    size: search.size ?? 10,
    status: search.status ?? "all",
  }),
  loader: async ({ context, params, deps }) => {
    const projectData = await context.queryClient.query(
      context.trpc.project.getById.queryOptions({
        projectId: params.id,
      })
    );

    await context.queryClient
      .query(
        context.trpc.agents.getById.queryOptions({
          projectId: params.id,
          agentId: params.agentId,
        })
      )
      .catch(() => undefined);

    if (projectData?.project?.organizationId) {
      await context.queryClient
        .query(
          context.trpc.organization.getById.queryOptions({
            id: projectData.project.organizationId,
          })
        )
        .catch(() => undefined);
    }

    await context.queryClient
      .query(
        context.trpc.agents.listRuns.queryOptions({
          projectId: params.id,
          agentId: params.agentId,
          page: deps.page,
          pageSize: deps.size,
          status:
            deps.status === "all" ? undefined : deps.status,
        })
      )
      .catch(() => undefined);
  },
  component: AgentDetailPage,
  head: () => ({
    meta: [
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

function AgentDetailPage() {
  const now = useCurrentTime();
  const { id: projectId, agentId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate({
    from: "/app/project/$id/agents/$agentId/",
  });
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [editDialogOpen, setEditDialogOpen] =
    useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] =
    useState(false);

  // Derive pagination from URL search params with defaults
  const page = search.page ?? 1;
  const size = search.size ?? 10;
  const statusFilter = search.status ?? "all";

  const pagination = useMemo(
    () => ({ pageIndex: page - 1, pageSize: size }),
    [page, size]
  );

  const setPagination = useCallback(
    (newPagination: {
      pageIndex: number;
      pageSize: number;
    }) => {
      void navigate({
        search: (prev) => ({
          ...prev,
          page: newPagination.pageIndex + 1,
          size: newPagination.pageSize,
          status: statusFilter,
        }),
        replace: true,
      });
    },
    [navigate, statusFilter]
  );

  const setStatusFilter = useCallback(
    (newStatus: AgentRunStatusValue | "all") => {
      void navigate({
        search: (prev) => ({
          ...prev,
          status: newStatus,
          page: 1,
          size,
        }),
        replace: true,
      });
    },
    [navigate, size]
  );

  const { data: projectData } = useSuspenseQuery(
    trpc.project.getById.queryOptions({ projectId })
  );

  const { data: organizationData } = useSuspenseQuery(
    trpc.organization.getById.queryOptions({
      id: projectData?.project?.organizationId ?? "",
    })
  );

  const { data: agentData } = useSuspenseQuery(
    trpc.agents.getById.queryOptions({ projectId, agentId })
  );

  const isPushAgent =
    agentData?.agent?.template?.type ===
    AgentTypes.OtelReceiver;

  const isAzureDevOpsAgent =
    agentData?.agent?.template?.type ===
    AgentTypes.AzureDevOpsPipeline;
  const azureConfig = isAzureDevOpsAgent
    ? (agentData?.agent
        ?.configuration as AzureDevOpsPipelineAgentConfig)
    : null;

  const isK8sIssuesAgent =
    agentData?.agent?.template?.type ===
    AgentTypes.K8sIssues;
  const isK8sOptimizationAgent =
    agentData?.agent?.template?.type ===
    AgentTypes.K8sOptimization;
  const isK8sAgent =
    isK8sIssuesAgent || isK8sOptimizationAgent;
  const k8sConfig = isK8sIssuesAgent
    ? (agentData?.agent
        ?.configuration as K8sIssuesAgentConfig)
    : isK8sOptimizationAgent
      ? (agentData?.agent
          ?.configuration as K8sOptimizationAgentConfig)
      : null;

  // Fetch connection details for Azure DevOps agents
  const { data: connectionData } = useQuery({
    ...trpc.azureDevOps.getById.queryOptions({
      organizationId:
        projectData?.project?.organizationId ?? "",
      connectionId: azureConfig?.connectionId ?? "",
    }),
    enabled:
      isAzureDevOpsAgent &&
      !!azureConfig?.connectionId &&
      !!projectData?.project?.organizationId,
  });

  const { data: clustersData } = useQuery({
    ...trpc.k8sClusters.list.queryOptions({
      organizationId:
        projectData?.project?.organizationId ?? "",
    }),
    enabled:
      isK8sAgent &&
      !!k8sConfig?.clusterIds?.length &&
      !!projectData?.project?.organizationId,
  });

  // Filter clusters to only show the ones configured for this agent
  const monitoredClusters = useMemo(() => {
    if (!clustersData?.clusters || !k8sConfig?.clusterIds)
      return [];
    return clustersData.clusters.filter((c) =>
      k8sConfig.clusterIds.includes(c.id)
    );
  }, [clustersData, k8sConfig]);

  // First page query - prefetched in loader for SSR hydration
  // This ensures hasActiveRun detection works immediately without client-side fetch.
  // When user is on page 1 with no filters, TanStack Query cache deduplicates this
  // with runsData below (same query key). On other pages, this stays cached.
  const { data: firstPageRuns, refetch: refetchFirstPage } =
    useSuspenseQuery(
      trpc.agents.listRuns.queryOptions({
        projectId,
        agentId,
        page: 1,
        pageSize: 10,
      })
    );

  // Detect if there's already an active run (pending or running)
  // Uses first page data which is always available from SSR
  const hasActiveRun = (firstPageRuns?.runs ?? []).some(
    (run) =>
      run.status === AgentRunStatus.Pending ||
      run.status === AgentRunStatus.Running
  );

  // Paginated runs for table (with filters and auto-refresh)
  const { data: runsData, refetch: refetchRuns } = useQuery(
    {
      ...trpc.agents.listRuns.queryOptions({
        projectId,
        agentId,
        page: pagination.pageIndex + 1,
        pageSize: pagination.pageSize,
        status:
          statusFilter === "all" ? undefined : statusFilter,
      }),
      refetchInterval: (query) => {
        if (isPushAgent) return 15_000;
        const runs = query.state.data?.runs ?? [];
        const isRunActive = runs.some(
          (run) =>
            run.status === AgentRunStatus.Pending ||
            run.status === AgentRunStatus.Running
        );

        return isRunActive ? 5000 : 30_000;
      },
      refetchIntervalInBackground: false,
    }
  );

  const triggerRunMutation = useMutation(
    trpc.agents.triggerRun.mutationOptions({
      onSuccess: () => {
        toast.success("Agent run triggered successfully");
        // Immediately refetch runs to show the new run
        void refetchFirstPage();
        void refetchRuns();
        void queryClient.invalidateQueries({
          queryKey:
            trpc.agents.getDashboardStats.queryKey(),
        });
      },
      onError: (error) => {
        toast.error(
          error.message ?? "Failed to trigger agent run"
        );
      },
    })
  );

  const toggleEnabledMutation = useMutation(
    trpc.agents.toggleEnabled.mutationOptions({
      onSuccess: (data) => {
        toast.success(
          data.agent?.isEnabled
            ? "Agent enabled"
            : "Agent disabled"
        );
        void queryClient.invalidateQueries({
          queryKey: trpc.agents.getById.queryKey({
            projectId,
            agentId,
          }),
        });
        void queryClient.invalidateQueries({
          queryKey: trpc.agents.listByProject.queryKey({
            projectId,
          }),
        });
      },
      onError: (error) => {
        toast.error(
          error.message ?? "Failed to toggle agent"
        );
      },
    })
  );

  const projectName = String(
    projectData?.project?.name ?? ""
  );
  const agent = agentData?.agent;
  const agentName = String(agent?.name ?? "");
  const isEnabled = Boolean(agent?.isEnabled);
  const scheduleExpr = String(
    agent?.scheduleExpression ?? ""
  );
  const nextRunAt = agent?.nextRunAt;
  const lastRunAt = agent?.lastRunAt;
  const runs = runsData?.runs ?? [];
  const totalRuns = runsData?.total ?? 0;

  const getScheduleLabel = (expr: string) => {
    if (expr === SchedulePresets.Manual)
      return "Manual only";
    if (expr === SchedulePresets.EveryMinute)
      return "Every minute";
    if (expr === SchedulePresets.Every5Minutes)
      return "Every 5 minutes";
    if (expr === SchedulePresets.EveryHour)
      return "Every hour";
    if (expr === SchedulePresets.Every6Hours)
      return "Every 6 hours";
    if (expr === SchedulePresets.Every12Hours)
      return "Every 12 hours";
    if (expr === SchedulePresets.Daily) return "Daily";
    return expr || "Not set";
  };

  const formatRelativeTime = (
    date: Date | null
  ): string => {
    if (!date) return "Never";
    const diff = now - new Date(date).getTime();
    const seconds = Math.floor(diff / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (seconds < 60) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days === 1) return "Yesterday";
    return `${days}d ago`;
  };

  const formatNextRun = (date: Date | null): string => {
    if (!date) return "Not scheduled";
    const diff = new Date(date).getTime() - now;
    if (diff < 0) return "Due now";
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);

    if (hours > 0) {
      const remainingMinutes = minutes % 60;
      if (remainingMinutes > 0) {
        return `in ${hours}h ${remainingMinutes}m`;
      }
      return `in ${hours}h`;
    }
    return `in ${minutes}m`;
  };

  const getStatusType = (
    status: string
  ): "completed" | "running" | "failed" | "pending" => {
    if (status === AgentRunStatus.Completed)
      return "completed";
    if (status === AgentRunStatus.Running) return "running";
    if (status === AgentRunStatus.Failed) return "failed";
    return "pending";
  };

  const getIssueCount = (
    result: AgentRunResult | null
  ): number | null => {
    if (!result) return null;
    if ("totalIssues" in result) return result.totalIssues;
    if ("pipelinesFailing" in result)
      return result.pipelinesFailing;
    if ("issuesFound" in result) return result.issuesFound;
    return null;
  };

  const getOutcomeStyle = (
    outcome: AgentRunOutcomeValue | null
  ): { label: string; className: string } => {
    switch (outcome) {
      case AgentRunOutcome.Success:
        return {
          label: "Success",
          className: "text-success-foreground",
        };
      case AgentRunOutcome.Partial:
        return {
          label: "Partial",
          className: "text-warning-foreground",
        };
      case AgentRunOutcome.Failure:
        return {
          label: "Failed",
          className: "text-destructive",
        };
      default:
        return {
          label: "-",
          className: "text-muted-foreground",
        };
    }
  };

  // Get last run status for the info card
  const lastRun = runs.length > 0 ? runs[0] : null;

  const columns: DataTableColumnDef<AgentRun>[] = [
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <StatusDot
            status={getStatusType(
              String(row.original.status)
            )}
            size="md"
          />
          <span className="text-sm capitalize">
            {String(row.original.status).toLowerCase()}
          </span>
        </div>
      ),
    },
    {
      accessorKey: "startedAt",
      header: "Started",
      cell: ({ row }) => (
        <ClientDate
          variant="small"
          date={row.original.startedAt}
          formatter={(d) => {
            const isToday =
              d.toDateString() ===
              new Date(now).toDateString();
            const yesterday = new Date(now);
            yesterday.setDate(yesterday.getDate() - 1);
            const isYesterday =
              d.toDateString() === yesterday.toDateString();

            const time = d.toLocaleTimeString(undefined, {
              hour: "2-digit",
              minute: "2-digit",
            });

            if (isToday) return `Today, ${time}`;
            if (isYesterday) return `Yesterday, ${time}`;

            const datePart = d.toLocaleDateString(
              undefined,
              {
                month: "short",
                day: "numeric",
              }
            );
            return `${datePart}, ${time}`;
          }}
        />
      ),
    },
    {
      id: "duration",
      header: "Duration",
      cell: ({ row }) => {
        const { startedAt, completedAt } = row.original;
        const durationMs = calculateDuration(
          startedAt,
          completedAt
        );
        return (
          <div className="text-sm tabular-nums">
            {formatDuration(durationMs)}
          </div>
        );
      },
    },
    {
      id: "issues",
      header: "Issues",
      cell: ({ row }) => {
        const { result, status } = row.original;
        // Only show issues for completed runs
        if (status !== AgentRunStatus.Completed) {
          return (
            <span className="text-sm text-muted-foreground">
              -
            </span>
          );
        }
        const count = getIssueCount(result);
        if (count === null) {
          return (
            <span className="text-sm text-muted-foreground">
              -
            </span>
          );
        }
        return (
          <span
            className={`text-sm tabular-nums ${count > 0 ? "font-medium text-destructive" : "text-muted-foreground"}`}
          >
            {count}
          </span>
        );
      },
    },
    {
      accessorKey: "outcome",
      header: "Outcome",
      cell: ({ row }) => {
        const { outcome, status } = row.original;
        // Only show outcome for completed runs
        if (status !== AgentRunStatus.Completed) {
          return (
            <span className="text-sm text-muted-foreground">
              -
            </span>
          );
        }
        const style = getOutcomeStyle(outcome);
        return (
          <span className={`text-sm ${style.className}`}>
            {style.label}
          </span>
        );
      },
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => (
        <Link
          to="/app/project/$id/agents/$agentId/runs/$runId"
          params={{
            id: projectId,
            agentId,
            runId: row.original.id,
          }}
        >
          <Button
            variant="ghost"
            size="xs"
            spacing="compact"
          >
            View
            <IconChevronRight className="size-3" />
          </Button>
        </Link>
      ),
    },
  ];

  const breadcrumbItems = [
    {
      label: organizationData?.organization?.name ?? "",
      to: "/app/organization/$id",
      params: {
        id: projectData?.project?.organizationId ?? "",
      },
    },
    {
      label: projectName,
      to: "/app/project/$id",
      params: { id: projectId },
    },
    {
      label: agentName,
      to: "/app/project/$id/agents/$agentId",
      params: { id: projectId, agentId },
    },
  ];

  return (
    <AppLayout
      title={<PageBreadcrumb items={breadcrumbItems} />}
    >
      <div className="space-y-6">
        {/* Agent Header */}
        <AgentHeader
          agentName={agentName}
          hasActiveRun={hasActiveRun}
          isEnabled={isEnabled}
          isRunning={triggerRunMutation.isPending}
          isToggling={toggleEnabledMutation.isPending}
          onDelete={() => setDeleteDialogOpen(true)}
          onEdit={() => setEditDialogOpen(true)}
          onRunNow={() =>
            triggerRunMutation.mutate({
              projectId,
              agentId,
            })
          }
          onToggleEnabled={() =>
            toggleEnabledMutation.mutate({
              projectId,
              agentId,
            })
          }
          projectId={projectId}
        />

        {/* Info Cards */}
        <div
          className={`grid grid-cols-1 gap-4 ${
            isAzureDevOpsAgent || isK8sAgent
              ? "md:grid-cols-2 lg:grid-cols-4"
              : "md:grid-cols-3"
          }`}
        >
          <Card variant="compact">
            <CardContent variant="p4">
              <div className="flex items-start gap-3">
                <div className="flex size-9 shrink-0 items-center justify-center bg-muted text-muted-foreground">
                  <IconCalendar className="size-4" />
                </div>
                <div className="min-w-0">
                  <div className="mb-0.5 text-xs text-muted-foreground">
                    Schedule
                  </div>
                  <div className="font-medium">
                    {getScheduleLabel(scheduleExpr)}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card variant="compact">
            <CardContent variant="p4">
              <div className="flex items-start gap-3">
                <div className="flex size-9 shrink-0 items-center justify-center bg-muted text-muted-foreground">
                  <IconClock className="size-4" />
                </div>
                <div className="min-w-0">
                  <div className="mb-0.5 text-xs text-muted-foreground">
                    Next Run
                  </div>
                  <div className="font-medium tabular-nums">
                    {isEnabled
                      ? formatNextRun(nextRunAt)
                      : "Disabled"}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Last Run Card */}
          <Card variant="compact">
            <CardContent variant="p4">
              <div className="flex items-start gap-3">
                <div className="flex size-9 shrink-0 items-center justify-center bg-muted text-muted-foreground">
                  <IconHistory className="size-4" />
                </div>
                <div className="min-w-0">
                  <div className="mb-0.5 text-xs text-muted-foreground">
                    Last Run
                  </div>
                  <div className="flex items-center gap-2">
                    {lastRun && (
                      <StatusDot
                        status={getStatusType(
                          String(lastRun.status)
                        )}
                        size="sm"
                      />
                    )}
                    <span className="font-medium">
                      {formatRelativeTime(lastRunAt)}
                    </span>
                  </div>
                  {lastRun?.startedAt &&
                    lastRun?.completedAt && (
                      <div className="mt-0.5 text-xs text-muted-foreground">
                        Duration:{" "}
                        {formatDuration(
                          new Date(
                            lastRun.completedAt
                          ).getTime() -
                            new Date(
                              lastRun.startedAt
                            ).getTime()
                        )}
                      </div>
                    )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Configuration Card - Azure DevOps only */}
          {isAzureDevOpsAgent && (
            <Card
              variant="interactive"
              className="cursor-pointer"
              onClick={() => setEditDialogOpen(true)}
            >
              <CardContent variant="p4">
                <div className="flex items-start gap-3">
                  <div className="flex size-9 shrink-0 items-center justify-center bg-muted text-muted-foreground">
                    <IconBrandAzure className="size-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="mb-0.5 text-xs text-muted-foreground">
                      Monitoring
                    </div>
                    {azureConfig?.connectionId ? (
                      <>
                        <div
                          className="truncate font-medium"
                          title={
                            connectionData?.connection
                              ?.name ?? "Loading..."
                          }
                        >
                          {connectionData?.connection
                            ?.name ?? "Loading..."}
                        </div>
                        <div className="mt-0.5 text-xs text-muted-foreground">
                          {(() => {
                            const totalPipelines =
                              azureConfig.projects?.reduce(
                                (sum, p) =>
                                  sum +
                                  (p.pipelineIds?.length ??
                                    0),
                                0
                              ) ?? 0;
                            const projectCount =
                              azureConfig.projects
                                ?.length ?? 0;
                            return `${totalPipelines} pipeline${totalPipelines !== 1 ? "s" : ""} across ${projectCount} project${projectCount !== 1 ? "s" : ""}`;
                          })()}
                        </div>
                      </>
                    ) : (
                      <div className="text-muted-foreground">
                        Not configured
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {isK8sAgent && (
            <Card
              variant="interactive"
              className="cursor-pointer"
              onClick={() => setEditDialogOpen(true)}
            >
              <CardContent variant="p4">
                <div className="flex items-start gap-3">
                  <div className="flex size-9 shrink-0 items-center justify-center bg-muted text-muted-foreground">
                    <IconServer className="size-4" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="mb-0.5 text-xs text-muted-foreground">
                      {k8sConfig?.clusterIds?.length === 1
                        ? "Cluster"
                        : "Clusters"}
                    </div>
                    {k8sConfig?.clusterIds?.length ? (
                      <>
                        <div
                          className="truncate font-medium"
                          title={
                            monitoredClusters.length > 0
                              ? monitoredClusters
                                  .map((c) => c.name)
                                  .join(", ")
                              : "Loading..."
                          }
                        >
                          {monitoredClusters.length > 0
                            ? monitoredClusters.length === 1
                              ? monitoredClusters[0]?.name
                              : `${monitoredClusters.length} clusters`
                            : "Loading..."}
                        </div>
                        {k8sConfig.namespaces &&
                          k8sConfig.namespaces.length >
                            0 && (
                            <div className="mt-0.5 text-xs text-muted-foreground">
                              {k8sConfig.namespaces.length}{" "}
                              namespace
                              {k8sConfig.namespaces
                                .length !== 1
                                ? "s"
                                : ""}
                            </div>
                          )}
                        {(!k8sConfig.namespaces ||
                          k8sConfig.namespaces.length ===
                            0) && (
                          <div className="mt-0.5 text-xs text-muted-foreground">
                            All namespaces
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-muted-foreground">
                        Not configured
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {isPushAgent && (
          <OtelSetupSection
            agentId={agentId}
            apiKey={
              (
                agent?.configuration as
                  | OtelReceiverAgentConfig
                  | undefined
              )?.apiKey ?? ""
            }
          />
        )}

        {/* Run History */}
        <section>
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-sm font-medium text-muted-foreground">
              Run History
            </h2>
            <Select
              value={statusFilter}
              onValueChange={(value) => {
                setStatusFilter(
                  value as AgentRunStatusValue | "all"
                );
              }}
            >
              <SelectTrigger className="w-35">
                <SelectValue placeholder="Filter status">
                  {statusFilter === "all"
                    ? "All statuses"
                    : statusFilter.charAt(0).toUpperCase() +
                      statusFilter.slice(1)}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  All statuses
                </SelectItem>
                <SelectItem
                  value={AgentRunStatus.Completed}
                >
                  Completed
                </SelectItem>
                <SelectItem value={AgentRunStatus.Running}>
                  Running
                </SelectItem>
                <SelectItem value={AgentRunStatus.Pending}>
                  Pending
                </SelectItem>
                <SelectItem value={AgentRunStatus.Failed}>
                  Failed
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {runs.length === 0 &&
          pagination.pageIndex === 0 &&
          statusFilter === "all" ? (
            <Card variant="compact">
              <CardContent variant="mutedCenter12">
                <p>No runs yet.</p>
                <p className="mt-1 text-xs">
                  {isPushAgent
                    ? "Runs are created when OTLP data is received."
                    : "The agent will run according to its schedule."}
                </p>
              </CardContent>
            </Card>
          ) : runs.length === 0 ? (
            <Card variant="compact">
              <CardContent variant="mutedCenter12">
                <p>No runs match this filter.</p>
              </CardContent>
            </Card>
          ) : (
            <DataTable
              columns={columns}
              data={runs}
              enablePagination
              pagination={pagination}
              onPaginationChange={setPagination}
              pageCount={
                Math.ceil(
                  totalRuns / pagination.pageSize
                ) || 1
              }
            />
          )}
        </section>
      </div>

      {/* Edit Agent Dialog */}
      {agent && (
        <EditAgentDialog
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          projectId={projectId}
          organizationId={String(
            projectData?.project?.organizationId ?? ""
          )}
          agent={agent}
        />
      )}

      {/* Delete Agent Dialog */}
      <DeleteAgentDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        projectId={projectId}
        agentId={agentId}
        agentName={agentName}
      />
    </AppLayout>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<
    ReturnType<typeof setTimeout> | undefined
  >(undefined);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const handleCopy = () => {
    void navigator.clipboard.writeText(text);
    setCopied(true);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(
      () => setCopied(false),
      2000
    );
  };

  return (
    <Button
      variant="ghost"
      size="icon-sm"
      onClick={handleCopy}
      title="Copy to clipboard"
    >
      {copied ? (
        <IconCheck className="size-3.5 text-success-foreground" />
      ) : (
        <IconCopy className="size-3.5" />
      )}
    </Button>
  );
}

function OtelSetupSection({
  agentId,
  apiKey,
}: {
  agentId: string;
  apiKey: string;
}) {
  const baseUrl = env.VITE_BASE_URL;
  const otelBase = `${baseUrl}/api/otel/${agentId}`;
  const logsEndpoint = `${otelBase}/v1/logs`;
  const tracesEndpoint = `${otelBase}/v1/traces`;
  const bearerHeader = `Bearer ${apiKey}`;

  const collectorYaml = `exporters:
  otlphttp/vivus:
    endpoint: ${otelBase}
    headers:
      Authorization: "Bearer ${apiKey}"

processors:
  resource/vivus:
    attributes:
      - key: vivus.github.repo
        value: "owner/repo-name"
        action: upsert

service:
  pipelines:
    logs:
      processors: [resource/vivus]
      exporters: [otlphttp/vivus]
    traces:
      processors: [resource/vivus]
      exporters: [otlphttp/vivus]`;

  const [yamlCopied, setYamlCopied] = useState(false);
  const [open, setOpen] = useState(false);
  const yamlTimerRef = useRef<
    ReturnType<typeof setTimeout> | undefined
  >(undefined);

  const handleCopyYaml = () => {
    void navigator.clipboard.writeText(collectorYaml);
    setYamlCopied(true);
    clearTimeout(yamlTimerRef.current);
    yamlTimerRef.current = setTimeout(
      () => setYamlCopied(false),
      2000
    );
  };

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card variant="compact">
        <CollapsibleTrigger
          variant="padded"
          className="flex w-full cursor-pointer items-center justify-between"
        >
          <h2 className="text-sm font-medium">
            Collector Setup
          </h2>
          <IconChevronDown
            className={`size-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`}
          />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent variant="borderP4">
            <div className="space-y-1.5">
              <div className="text-xs font-medium text-muted-foreground">
                Endpoints
              </div>
              <div className="flex items-center justify-between rounded border bg-muted/50 px-3 py-1.5">
                <div className="min-w-0">
                  <span className="text-xs text-muted-foreground">
                    Logs
                  </span>
                  <div className="truncate font-mono text-xs">
                    {logsEndpoint}
                  </div>
                </div>
                <CopyButton text={logsEndpoint} />
              </div>
              <div className="flex items-center justify-between rounded border bg-muted/50 px-3 py-1.5">
                <div className="min-w-0">
                  <span className="text-xs text-muted-foreground">
                    Traces
                  </span>
                  <div className="truncate font-mono text-xs">
                    {tracesEndpoint}
                  </div>
                </div>
                <CopyButton text={tracesEndpoint} />
              </div>
            </div>

            <div className="space-y-1.5">
              <div className="text-xs font-medium text-muted-foreground">
                Authorization Header
              </div>
              <div className="flex items-center justify-between rounded border bg-muted/50 px-3 py-1.5">
                <code className="truncate font-mono text-xs">
                  Authorization: {bearerHeader}
                </code>
                <CopyButton
                  text={`Authorization: ${bearerHeader}`}
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <div className="text-xs font-medium text-muted-foreground">
                  Collector config snippet
                </div>
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={handleCopyYaml}
                >
                  {yamlCopied ? (
                    <>
                      <IconCheck className="size-3 text-success-foreground" />
                      Copied
                    </>
                  ) : (
                    <>
                      <IconCopy className="size-3" />
                      Copy
                    </>
                  )}
                </Button>
              </div>
              <pre className="overflow-x-auto rounded border bg-muted/50 p-3 font-mono text-xs leading-relaxed">
                {collectorYaml}
              </pre>
            </div>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}
